/**
 * Centralized logout function that handles all cleanup for both SSO and Cognito users.
 * This ensures consistent logout behavior across the entire application.
 *
 * @param reason - Optional reason for logout (for logging/debugging)
 * @param dispatch - Optional Redux dispatch function. If provided, will dispatch removeAuth() directly.
 *                   If not provided, the 'auth:logout' event will be emitted and _app.tsx listener will handle Redux.
 */
import { Auth } from 'aws-amplify'
import { Dispatch } from 'react'
import { AMPLIFY_USER_POOL_APP_CLIENT_ID } from 'src/config/config'
import { resetTokenRefreshGuard } from 'src/hooks/tokenRefreshGuard'
import { resetApolloClient } from 'src/hooks/useApolloClient'
import useClearCookiesExceptConsent from 'src/hooks/useClearCookiesExceptConsent'
import { removeAuth } from 'src/store/actions/auth'

const isSSO = (): boolean => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('sso') === 'true'
  } catch {
    return false
  }
}

export const logout = async (reason?: string, dispatch?: Dispatch<any>): Promise<void> => {
  if (typeof window === 'undefined') {
    return
  }

  // Log reason if provided (for debugging)
  if (reason) {
    // eslint-disable-next-line no-console
    console.log(`Logout triggered: ${reason}`)
  }

  const _isSSO = isSSO()

  // 1. Clear Redux state first (so UI updates immediately)
  if (dispatch) {
    dispatch(removeAuth())
  }

  // 2. Clear localStorage tokens (this will trigger storage events for other tabs)
  if (_isSSO) {
    localStorage.removeItem('idToken')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('sso')
  } else {
    // Non-SSO: remove Cognito-prefixed keys
    const appClientId = AMPLIFY_USER_POOL_APP_CLIENT_ID
    if (appClientId) {
      Object.keys(localStorage)
        .filter(
          (k) =>
            k.includes('CognitoIdentityServiceProvider') &&
            k.includes(appClientId) &&
            (k.endsWith('.idToken') || k.endsWith('.accessToken') || k.endsWith('.refreshToken')),
        )
        .forEach((key) => localStorage.removeItem(key))
    }
    // Also call Auth.signOut() for Cognito users
    try {
      await Auth.signOut()
    } catch (error) {
      // Ignore errors if user is already signed out
      // eslint-disable-next-line no-console
      console.warn('Auth.signOut() error (may be expected):', error)
    }
  }

  // 3. Clear cookies (except consent)
  useClearCookiesExceptConsent()

  // 4. Reset token refresh guard
  resetTokenRefreshGuard()

  // 5. Reset Apollo client
  resetApolloClient()

  // 6. Dispatch custom event for same-tab Redux update (if dispatch wasn't provided)
  // (storage event only fires in other tabs)
  // If dispatch was provided, we already updated Redux, but still emit event for consistency
  window.dispatchEvent(new Event('auth:logout'))
}

/**
 * Helper function that performs logout and redirects to signin page.
 * Use this when you want to logout and redirect in one call.
 *
 * @param reason - Optional reason for logout (for logging/debugging)
 * @param redirectPath - Path to redirect to after logout (default: '/signin')
 * @param dispatch - Optional Redux dispatch function
 */
export const logoutAndRedirect = async (
  reason?: string,
  redirectPath: string = '/signin',
  dispatch?: Dispatch<any>,
): Promise<void> => {
  await logout(reason, dispatch)
  if (typeof window !== 'undefined') {
    window.location.href = redirectPath
  }
}
